<template>

  <Page>
    <BaseHeroScroll imageURL="/images/home-hero.jpg" class="text-white text-shadow flex flex-col items-center justify-center">
    <div>
      <Logo class="inline-block md:absolute md:top-0 md:left-0 w-32 lg:w-40 xl:w-48 h-auto mt-6 ml-0 md:ml-6 mr-6">
        <a href="https://www.kweliterealtors.com/" target="_BLANK">
          <img src="/images/kwelite2.png" alt="KW Elite, Realtors - Keller Williams">
        </a>
      </Logo>
      <Logo class="inline-block md:absolute md:top-0 md:right-0 w-16 lg:w-24 xl:w-24 h-auto mt-6 mr-0 md:mr-6">
          <img src="/images/angela-sielski-group-w.png" alt="Angela Sielski Group">
      </Logo>
    </div>
      <div class="px-6">
        <div class="text-center text-4xl sm:text-5xl font-semibold md:font-bold my-12 md:my-12 leading-tight">
          Helping you move up or move on.
        </div>
        <div class="flex justify-center items-center space-x-8">
          <img src="/images/angelasielski.jpg" class="transform scale-90 rounded-full w-32 h-32" alt="Photo of Angela Sielski.">
          <div class="flex flex-col -space-y-1">
            <BaseHeading :level="1">Angela Sielski</BaseHeading>
            <div class="text-2xl italic">Realtor®</div>
          </div>
        </div>
      </div>
    </BaseHeroScroll>

    <div class="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-10 bg-kwlightgray p-10">
      <a class="text-center border-b-4 border-red-900 text-white bg-kwred underline px-8 py-4 text-xl lg:text-2xl font-semibold rounded shadow-md hover:scale-105 hover:shadow-sm transform transition duration-500 ease-in-out" href="Angela Sielski - Buyers Guide.pdf">Buyers Guide</a>
      <a class="text-center border-b-4 border-red-900 text-white bg-kwred underline px-8 py-4 text-xl lg:text-2xl font-semibold rounded shadow-md hover:scale-105 hover:shadow-sm transform transition duration-500 ease-in-out" href="https://angela-sielski.kw.com">Find Your Dream Home</a>
    </div>

    <Section>
      <div class="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
        <div class="col-span-1 lg:col-span-2 xl:col-span-3 border-0 lg:border-r-2 pr-0 lg:pr-10 mb-4 lg:mb-0">
          <AboutMe />
        </div>
        <div class="col-span-1 lg:col-span-1 xl:col-span-1 pl-0 lg:pl-10">
          <div class="grid grid-cols-3 lg:grid-cols-1 pb-12">
            <img src="/images/coe2023gold.png" class="w-40 mx-auto lg:pt-6" alt="New Jersey Realtors® Circle of Excellence Sales Award - Gold Level - 2023" />
            <img src="/images/coe2022gold.png" class="w-40 mx-auto lg:pt-6" alt="New Jersey Realtors® Circle of Excellence Sales Award - Gold Level - 2022" />
            <img src="/images/coe2021gold.png" class="w-40 mx-auto lg:pt-6" alt="New Jersey Realtors® Circle of Excellence Sales Award - Gold Level - 2021" />
            <img src="/images/coe2020bronze.png" class="w-40 mx-auto lg:pt-6" alt="New Jersey Realtors® Circle of Excellence Sales Award - Bronze Level - 2020" />
          </div>
          <BaseHeading :level="5">Contact</BaseHeading>
          <ContactCard />
        </div>
      </div>
    </Section>

    <Section class="bg-grad">
      <BaseHeading :level="2">Featured Listings</BaseHeading>
      <BaseGrid :columns="3" class="gap-6">
        <PropertyCard v-for="property in featured" :key="property.id"
          :address="`${property.attributes.field_address.address_line1}, ${property.attributes.field_address.locality}, ${property.attributes.field_address.administrative_area} ${getZip(property.attributes.field_address.postal_code)}`"
          :price="getFeaturedPrice(property)"
          :tagline="getStatus(property.relationships.field_status.data.id)"
          :imageURL="getImage(property.relationships.field_images.data)"
          :listingURL="getLink(property)"
          :beds="property.attributes.field_bedrooms"
          :baths="property.attributes.field_bathrooms"
          :sqft="property.attributes.field_square_feet"
        />
      </BaseGrid>
    </Section>

    <Section class="bg-white">
      <BaseHeading :level="3">Testimonials</BaseHeading>
      <BaseGrid :columns="2" class="col-gap-32 row-gap-10 md:row-gap-16">
        <Testimonial quote="We had an incredible experience working with Angela. She is very patient and works for your needs and wants. If you're looking for someone who is overly knowledgeable of the central jersey market you have to go with Angela as a buyer.&quot;" name="Zillow Reviewer who bought a home in 2020 in Scotch Plains, NJ." href="https://www.zillow.com/profile/ASielski-NJ/#reviews" />
        <Testimonial quote="Angela did a fantastic job helping us sell our house. She was very knowledgeable about the current market in town and about the process of selling a house. I would highly recommend her!!!!&quot;" name="Zillow Reviewer who sold a home in 2020 in Metuchen, NJ."  href="https://www.zillow.com/profile/ASielski-NJ/#reviews"  />
        <Testimonial quote="Angela was amazing from start to finish. From pricing, to staging, to inspection requests. I’m thrilled that we decided to work with her on our last sale. She knows the Metuchen market like no other. Her ability to guide her clients through any number of questions and worries is unmatched.&quot;"  name="Zillow Reviewer who sold a home in 2020 in Metuchen, NJ." href="https://www.zillow.com/profile/ASielski-NJ/#reviews"  />
        <Testimonial quote="Angela is hands down the best realtor to work with! She is extremely knowledgeable in all aspects of the process and goes above and beyond in all details!  She was extremely responsive and patient and truly cares about making you and your family happy!&quot;"  name="Zillow Reviewer who sold a home in 2020 in Metuchen, NJ." href="https://www.zillow.com/profile/ASielski-NJ/#reviews"  />
        <Testimonial quote="Angela is a highly knowledgeable and professional. She is an expert in her territory and also has a very extensive network for all facets of home buying. Furthermore, she is very accommodating, no better example than all of the help she had provided our family over these last months in this challenging environment. We were able to execute on our purchase and we could not be happier. She is everything you need in an agent. Lastly, she is a really wonderful person. Highly recommend.&quot;"  name="Zillow Reviewer who bought a home in 2020 in Metuchen, NJ." href="https://www.zillow.com/profile/ASielski-NJ/#reviews"  />
        <Testimonial quote="We can't say enough about Angela Sielski.  As first time home sellers, Angela walked us through the entire process.  She explained every step along the way.  If we did have a question or concern, she responded back quickly and answered all our questions.  She also gave us recommendations that assisted us in selling our home quickly and painlessly.  Angel was professional, thorough, and atop of the entire process.  We definitely would recommend Angela Sielski as your Real Estate Professional.&quot;"  name="Zillow Reviewer who sold a home in 2019 in Edison, NJ." href="https://www.zillow.com/profile/ASielski-NJ/#reviews"  />
      </BaseGrid>
      <div class="container text-center mt-10 md:mt-12 mb-6">
        <a class="text-kwred" href="http://www.zillow.com/profile/ASielski-NJ/#reviews" target="_blank" rel="nofollow">
          View all reviews.
        </a>
      </div>
    </Section>

    <Section class="bg-grad">
      <BaseHeading :level="4">Previous Sales</BaseHeading>
      <BaseGrid :columns="4" class="gap-6">
        <PropertyCard v-for="property in previous" :key="property.id"
          :address="`${property.attributes.field_address.address_line1}, ${property.attributes.field_address.locality}, ${property.attributes.field_address.administrative_area} ${getZip(property.attributes.field_address.postal_code)}`"
          :price="property.attributes.field_sold_price"
          :tagline="getStatus(property.relationships.field_status.data.id)"
          :closingDate="getClosingDate(property.attributes.field_sold_date)"
          :imageURL="getImage(property.relationships.field_images.data)"
        />
      </BaseGrid>
    </Section>

  </Page>

</template>

<script>
import config from '@/config'
import Page from '@/components/Page.vue'
import BaseHeroScroll from '@/components/BaseHeroScroll.vue'
import BaseGrid from '@/components/BaseGrid.vue'
import Logo from '@/components/Logo.vue'
import Section from '@/components/Section.vue'
import PropertyCard from '@/components/PropertyCard.vue'
import BaseHeading from '@/components/BaseHeading.vue'
import Testimonial from '@/components/Testimonial.vue'
import AboutMe from '@/components/AboutMe.vue'
import ContactCard from '@/components/ContactCard.vue'
import moment from 'moment'

export default {
  name: 'Home',
  components: {
    Page,
    BaseHeroScroll,
    BaseGrid,
    Logo,
    Section,
    PropertyCard,
    BaseHeading,
    Testimonial,
    AboutMe,
    ContactCard
  },
  data () {
    return {
      featured: [],
      previous: [],
      images: {},
      status: {}
    }
  },
  mounted () {
    this.$http
      .get(config.cmsUrl)
      .then(response => {
        for (var i of response.data.included) {
          if (i.type === 'file--file') {
            var mediumStyle = i.attributes.image_style_uri.filter(obj => obj.medium !== undefined)[0]
            this.images[i.id] = mediumStyle.medium
          } else if (i.type === 'taxonomy_term--status') {
            this.status[i.id] = i.attributes.name
          }
        }

        this.featured = response.data.data.filter((property) => {
          return property.attributes.promote
        }).sort((a, b) => {
          if (a.attributes.field_list_date > b.attributes.field_list_date) {
            return -1
          }
          if (a.attributes.field_list_date === b.attributes.field_list_date) {
            return 0
          }
          if (a.attributes.field_list_date < b.attributes.field_list_date) {
            return 1
          }
        })

        this.previous = response.data.data.filter((property) => {
          return this.status[property.relationships.field_status.data.id] === 'Closed'
        }, this).sort((a, b) => {
          if (a.attributes.field_sold_date > b.attributes.field_sold_date) {
            return -1
          }
          if (a.attributes.field_sold_date === b.attributes.field_sold_date) {
            return 0
          }
          if (a.attributes.field_sold_date < b.attributes.field_sold_date) {
            return 1
          }
        })
      })
  },
  computed: {
    getConfig () {
      return config
    }
  },
  methods: {
    formatBaths (baths) {
      return baths.replace('.50', '.5').replace('.00', '')
    },
    getImage (data) {
      if (data.length > 0) {
        return this.images[data[0].id]
      }
    },
    getZip (zip) {
      return zip.substring(0, 5)
    },
    getStatus (id) {
      return this.status[id]
    },
    getLink (property) {
      if (property.attributes.field_listing_link) {
        return property.attributes.field_listing_link.uri
      }
    },
    getClosingDate (date) {
      return 'Closed ' + moment(date).format('MMMM D, YYYY')
    },
    getFeaturedPrice (property) {
      if (this.status[property.relationships.field_status.data.id] === 'Closed') {
        return property.attributes.field_sold_price
      } else {
        return property.attributes.field_list_price
      }
    }
  }
}

</script>
<style scoped lang="scss">
.bg-grad {
  background: linear-gradient(#fafafa, #f0f0f0);
}
</style>
